import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, CheckCircle, KeyRound } from 'lucide-react';

export default function ResetPasswordPage() {
  const { resetPassword } = useAuth();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setLoading(true);
    const result = await resetPassword(token, password);
    setLoading(false);
    if (result.success) {
      setSuccess(true);
    } else {
      setError(result.error);
    }
  };

  if (!token) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-12 px-4" data-testid="reset-password-page">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 max-w-md text-center">
          <h1 className="text-xl font-bold text-[#1F2937] font-['Outfit']">Invalid Reset Link</h1>
          <p className="text-sm text-[#4B5563] mt-2">This password reset link is invalid or has expired.</p>
          <Link to="/forgot-password">
            <Button className="mt-4 bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl" data-testid="request-new-link-btn">
              Request New Link
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)] flex items-center justify-center py-12 px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 animate-fade-in-up">
          {success ? (
            <div className="text-center" data-testid="reset-success-message">
              <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-7 w-7 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Password Reset</h1>
              <p className="text-sm text-[#4B5563] mt-2">Your password has been successfully reset.</p>
              <Link to="/login">
                <Button className="mt-6 bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl" data-testid="go-to-login-btn">
                  Go to Sign In
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <div className="w-14 h-14 rounded-full bg-[#FFF0ED] flex items-center justify-center mx-auto mb-4">
                  <KeyRound className="h-6 w-6 text-[#F97D6A]" />
                </div>
                <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Set new password</h1>
                <p className="text-sm text-[#4B5563] mt-1">Must be at least 6 characters long.</p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3 mb-6" data-testid="reset-error">
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="reset-password-form">
                <div>
                  <Label htmlFor="password" className="text-[#1F2937] font-medium">New Password</Label>
                  <div className="relative mt-1.5">
                    <Input
                      id="password"
                      type={showPass ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter new password"
                      required
                      className="rounded-xl h-11 border-gray-200 focus:ring-[#F97D6A]/50 focus:border-[#F97D6A] pr-10"
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(!showPass)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirm" className="text-[#1F2937] font-medium">Confirm Password</Label>
                  <Input
                    id="confirm"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter new password"
                    required
                    className="mt-1.5 rounded-xl h-11 border-gray-200 focus:ring-[#F97D6A]/50 focus:border-[#F97D6A]"
                    data-testid="reset-confirm-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl h-11 font-semibold"
                  data-testid="reset-submit-btn"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
